import React, { useEffect, useRef, useState } from 'react'
import './my-org-view.styles.scss';
import BackButton from '@/components/SHARED/back-button/back-button.component';
import { useNavigate, useParams } from 'react-router-dom';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { useSelector } from 'react-redux';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisV, faEnvelope, faPlusCircle, faTrash, faUser } from '@fortawesome/free-solid-svg-icons';
import OrgInviteModal from '@/components/HOME/orgs/org-invite-modal/org-invite-modal.component';
import { format } from 'date-fns';
import { properCase } from '@/utils/properCase';
import { getEmailContent } from '@/utils/getEmailContent';
import MemberRoleModal from './member-role-modal.component';
import { changeDocumentTitle } from '@/utils/useDocumentTitle';

const MyOrgView = ({ orgs }) => {

    const userProfile = useSelector(state => state.userData.userProfile);
    const screenSize = useSelector(state => state.contentData.screenSize);
    const { orgPath } = useParams();
    const [ currentOrg, setCurrentOrg ] = useState(null);
    const [ currentMembers, setCurrentMembers ] = useState([]);
    const [ currentManagers, setCurrentManagers ] = useState([]);
    const [ alertMessage, setAlertMessage ] = useState(null);
    const [ confirmMessage, setConfirmMessage ] = useState(null);
    const [ invites, setInvites ] = useState([]);
    const [ loading, setLoading ] = useState(true);
    const [ showDropdown, setShowDropdown ] = useState(null);
    const [ showInviteModal, setShowInviteModal ] =  useState(false);
    const [ roleChangeMember, setRoleChangeMember ] = useState(null);
    const inviteRef = useRef();
    const managerRef = useRef();
    const approveRef = useRef();
    let inviteListener;
    const navigate = useNavigate();

    useEffect(() => {

        return () => {
            if (inviteListener) inviteListener();
        }
    }, [])

    useEffect(() => {
        if (!orgPath || !orgs) return;
        console.log(orgs);
        const selectedOrg = Object.values(orgs)
        .filter(o => o.path === orgPath)[0];
        if (!selectedOrg) {
            navigate('/home/myorgs');
            return;
        }
        setCurrentOrg({...selectedOrg});
        changeDocumentTitle(`Gamably | ${selectedOrg.name}`);

    }, [orgPath, orgs])

    useEffect(() => { 
        if (!currentOrg || !userProfile) return;
        getOrgData();
    }, [currentOrg, userProfile])

    async function getOrgData() {
        setInvites([]);
        const res = await UseCloudFunction(
            'getOrgData',
            {
                'orgId': currentOrg.orgId,
                'appId': userProfile.appId,
            }
        )
        console.log(res);
        if (res.error) {
            if (res.error === 'no access') {
                navigate(-1);
                return;
            }
            setAlertMessage('Something went wrong: ' + res.error);
            return;
        }
        let members = Object.values(res.members);
        console.log(members);
        let pendingInvites = Object.values(res.invites).map(i => i.email);
        console.log(pendingInvites);
        setCurrentMembers([...members].filter(m => !pendingInvites.includes(m.email)));
        for (let mem of members) {
            if (
                mem.roles.includes('manager') || 
                mem.roles.includes('owner') 
            ) {
                managerRef.current = {
                    ...managerRef.current,
                    ...{[mem.appId]: mem}
                }
                setCurrentManagers(managerRef.current);
            }
        }
        setLoading(false);
        setInvites(Object.values(res.invites));
    }

    function removeInvite(invite) {
        console.log('rescind invite');
        console.log(invite.appId);
        setConfirmMessage('Are you sure you want to remove this invite? If this user has been added as a manager or distributor to any games, they will lose access to those games. They will not lose access to any games for which they are owners.');
        approveRef.current = async () => {
            setShowDropdown(null);
            console.log('approve');
            console.log(invite.appId);
            setInvites([]);
            setLoading(true);
            const res = await UseCloudFunction(
                'removeInvite',
                {
                    'user': invite,
                    'orgId': currentOrg.orgId,
                    'inviteId': invite.id,
                    'ts': new Date().getTime(),
                    'appId': userProfile.appId
                }
            )
            console.log(res);
            if (res.error) {
                setAlertMessage(`Something went wrong. Error: ${res.error}`);
                setLoading(false);
                return;
            }
            setInvites(invites.filter(i => i.appId !== invite.appId));
            setConfirmMessage(null);
            setAlertMessage('Invite removed successfully.');
            setLoading(false);
        }
    }

    function resendEmail(email) {
        console.log('resend email');
        setConfirmMessage('Are you sure you want to resend this invite?');
        approveRef.current = async () => {
            setShowDropdown(null);
            const emailContent = getEmailContent(
                'memberInvite',
                {
                    'email': email,
                    'orgName': currentOrg.name,
                    'orgIcon': currentOrg.icon,
                    'managerName': `${userProfile.fName} ${userProfile.lName}`
                }
            )
            const res = await UseCloudFunction(
                'sendEmail',
                {
                    'emailData': emailContent,
                    'appId': userProfile.appId
                }
            )
            console.log(res);
            if (res.error) {
                setAlertMessage(`Something went wrong. Error: ${res.error}`);
                return;
            }
            setAlertMessage('Email sent successfully!');
        }
    }

    function removeMember(member) {
        setConfirmMessage('Are you sure you want to remove this member? They will also lose access to any games for which they are managers or distributors. Users will not be removed from games for which they are owners.');
        approveRef.current = async () => {
            continueRemove();
        }

        async function continueRemove() {
            setShowDropdown(null);
            setLoading(true);
            const res = await UseCloudFunction(
                'removeOrgMember',
                {
                    'appId': userProfile.appId,
                    'orgId': currentOrg.orgId,
                    'member': member
                }
            )
            console.log(res);
            if (res.error) {
                setAlertMessage(`Something went wrong. Error: ${res.error}`);
                setLoading(false);
                return;
            }
            setAlertMessage('Member removed successfully.'); 
            // getOrgData();
        }
    }

    function changeMemberRole(member) {
        setShowDropdown(null);
        setRoleChangeMember(member);
    }

    return (
        <div className={`my-org-view ${screenSize}`}>
            <div className={`g-card ${screenSize}`}>
                <BackButton cancel={() => navigate(-1)} />
                <div className='g-space-1'></div>
                {
                    (currentOrg && userProfile) &&
                    <div className='my-org-view-content'>
                        <div className='card-title'>
                            <ImageContainer 
                                src={currentOrg.icon ?? '/images/icons/placeholder-icon-org.jpg'}
                                className='head-icon' />
                            {currentOrg.name}
                        </div>
                        <hr />
                        <div>{currentOrg.name}</div>
                        <div>{currentOrg.address}</div>
                        <div>{currentOrg.address2}</div>
                        <div>{currentOrg.city}, {currentOrg.state} {currentOrg.zip}</div>
                        <div>{currentOrg.contactPhone}</div>
                        <div className='g-space-1'></div>
                        <div>Contact: <a href={`mailto:${currentOrg.contactEmail}`} target='_blank'>
                            {currentOrg.contactName}
                        </a>
                        </div>
                        {/* <div className='g-space-1'></div> */}
                        <hr />
                        <div className='title-with-button'>
                            <div className='info card-title'>
                                <div>Current Members</div>
                                <div className='meta'>Count: {currentMembers.length + invites.length}/{currentOrg.memberMax}</div>
                                {
                                    (screenSize !== 'mobile') &&
                                    <div></div>
                                }
                                {
                                    (currentManagers[userProfile.appId]) &&
                                    <button 
                                        className='g-button med-btn'
                                        onClick={() => setShowInviteModal(true)}
                                        >
                                        Add Members
                                        <FontAwesomeIcon icon={faPlusCircle} />
                                    </button>
                                }
                            </div>
                        </div>
                        <div className='g-space-1'></div>
                        {
                            (loading)
                            ?
                            <ThreeDotsLoader />
                            :
                            (currentMembers.length > 0) 
                            ?
                            <div className='member-list'>
                                {
                                    currentMembers
                                    .sort((a,b) => a.lName.toLowerCase() > b.lName.toLowerCase() ? 1 : -1)
                                    .map(m => (
                                        <div key={m.appId} className={`g-list-item member-list-item ${screenSize} ${currentManagers[userProfile.appId] ? 'manager' : ''}`}>
                                            <div>{m.lName}, {m.fName}</div>
                                            <div className='meta'>{m.email}</div>
                                            {
                                                // (screenSize === 'largeTablet' || screenSize === 'desktop') &&
                                                (
                                                    m.org_joinDates &&
                                                    m.org_joinDates.length > 0 &&
                                                    m.org_joinDates.filter(d => d.orgId === currentOrg.orgId).length > 0
                                                ) 
                                                ?
                                                <div className='meta'>Joined Org {format(new Date(m.org_joinDates.filter(d => d.orgId === currentOrg.orgId)[0].ts_joined), "P")}</div>
                                                :
                                                <div></div>
                                            }
                                            <div className={`meta ${screenSize !== 'mobile' ? 'right' : ''}`}>{m.roles.length > 1 ? properCase(m.roles.filter(m => m !== 'member').join(', ')) : 'Member'}</div>
                                            {
                                                (currentManagers[userProfile.appId]) &&
                                                <div className='buttons'>
                                                    <button 
                                                        className='g-button small-btn'
                                                        onClick={() => setShowDropdown(showDropdown !== m.appId ? m.appId : null)}>
                                                        <FontAwesomeIcon icon={faEllipsisV} />
                                                    </button>
                                                    <div className={`dropdown-menu member ${showDropdown === m.appId ? 'active' : ''}`}> 
                                                        <div className='dropdown-content'>
                                                            <button 
                                                                className={`g-button text-only ${m.roles.includes('owner') ? 'disabled' : ''}`}
                                                                onClick={() => changeMemberRole(m)}>
                                                                <FontAwesomeIcon icon={faUser} />
                                                                Change Role
                                                            </button>
                                                            <button 
                                                                className={`g-button text-only ${m.roles.includes('owner') ? 'disabled' : ''}`}
                                                                onClick={() => removeMember(m)}>
                                                                <FontAwesomeIcon icon={faTrash} />
                                                                Remove Member
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    ))
                                }
                            </div>
                            :
                            <div>No Members Yet...</div>
                        }
                        {
                            (invites.length > 0) &&
                            <div className='invites-div'>
                                <div className='g-space-2'></div>
                                <div className='card-title'>
                                    Pending Invites
                                </div>
                                <div className="g-space-1"></div>
                                {
                                    (Object.values(invites).length > 0)
                                    ?
                                    <div className='member-list'>
                                        {
                                            Object.values(invites)
                                            .filter(i => i.status === 'pending')
                                            .sort((a,b) => a.email > b.email ? 1 : -1)
                                            .map(m => (
                                                <div key={m.ts} className={`g-list-item member-list-item ${screenSize}`}>
                                                    <div>{m.email}</div>
                                                    <div className='meta'>Sent {format(new Date(m.ts), "P")}</div>
                                                    {
                                                        (currentManagers[userProfile.appId]) &&
                                                        <>
                                                        {
                                                            (screenSize !== 'mobile') &&
                                                            <div></div>
                                                        }
                                                        <div className='buttons'>
                                                            <button 
                                                                className='g-button small-btn'
                                                                onClick={() => setShowDropdown(showDropdown !== m.appId ? m.appId : null)}>
                                                                <FontAwesomeIcon icon={faEllipsisV} />
                                                            </button>
                                                            <div className={`dropdown-menu invite ${showDropdown === m.appId ? 'active' : ''}`}> 
                                                                <div className='dropdown-content'>
                                                                    <button 
                                                                        className='g-button text-only'
                                                                        onClick={() => resendEmail(m.email)}>
                                                                        <FontAwesomeIcon icon={faEnvelope} />
                                                                        Resend Email
                                                                    </button>
                                                                    <button 
                                                                        className='g-button text-only'
                                                                        onClick={() => removeInvite(m)} >                                                        
                                                                        <FontAwesomeIcon icon={faTrash} />
                                                                        Remove Invite
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        </>
                                                    }
                                                    
                                                </div>
                                            ))
                                        }
                                    </div>
                                    :
                                    <div>No pending invites...</div>
                                }
                            </div>
                        }
                    </div>
                }
            </div>
            <OrgInviteModal
                show={showInviteModal}
                cancel={() => setShowInviteModal(false)}
                orgData={currentOrg}
                invites={invites} />
            <MemberRoleModal
                show={roleChangeMember}
                cancel={() => setRoleChangeMember(null)}
                member={roleChangeMember}
                orgData={currentOrg}
                setAlertMessage={setAlertMessage}
                setConfirmMessage={setConfirmMessage}
                approveRef={approveRef} />
            <ModalAlert
                show={alertMessage}
                cancel={() => setAlertMessage(null)}
                message={alertMessage} />
            <ModalConfirm
                show={confirmMessage}
                cancel={() => setConfirmMessage(null)}
                message={confirmMessage}
                onApprove={approveRef.current} />
        </div>
    )
}

export default MyOrgView